/**
 * devSession — localStorage-backed pretend-login for placeholder backend mode.
 *
 * Activates ONLY when env.IS_PLACEHOLDER_BACKEND is true (no real Supabase
 * configured). Lets a developer/designer browse the authenticated parts of
 * the app without setting up a backend.
 *
 * In production / configured environments, none of this code is consulted —
 * the gating happens in the call sites via `env.IS_PLACEHOLDER_BACKEND`.
 */

export type DevRole = 'customer' | 'provider' | 'admin'

const STORAGE_KEY = 'hjt-dev-session'
const CHANGE_EVENT = 'hjt:dev-session-change'

export interface DevSession {
  role: DevRole
  userId: string
  email: string
  fullName: string
}

const FIXED_IDS: Record<DevRole, string> = {
  // Fixed UUID-shaped strings so memoized query keys stay stable across reloads.
  customer: '00000000-0000-0000-0000-00000000c001',
  provider: '00000000-0000-0000-0000-00000000d001',
  admin: '00000000-0000-0000-0000-0000000000ad',
}

const DEFAULTS: Record<DevRole, Pick<DevSession, 'email' | 'fullName'>> = {
  customer: { email: 'test-kund@hjalptorget.dev', fullName: 'Test Kund' },
  provider: { email: 'test-leverantor@hjalptorget.dev', fullName: 'Test Leverantör' },
  admin: { email: 'test-admin@hjalptorget.dev', fullName: 'Test Admin' },
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

export function getDevSession(): DevSession | null {
  if (!isBrowser()) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DevSession>
    if (
      parsed &&
      typeof parsed === 'object' &&
      (parsed.role === 'customer' || parsed.role === 'provider' || parsed.role === 'admin') &&
      typeof parsed.userId === 'string'
    ) {
      return {
        role: parsed.role,
        userId: parsed.userId,
        email: parsed.email ?? DEFAULTS[parsed.role].email,
        fullName: parsed.fullName ?? DEFAULTS[parsed.role].fullName,
      }
    }
    return null
  } catch {
    return null
  }
}

export function setDevSession(role: DevRole): DevSession {
  const session: DevSession = {
    role,
    userId: FIXED_IDS[role],
    email: DEFAULTS[role].email,
    fullName: DEFAULTS[role].fullName,
  }
  if (isBrowser()) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
  }
  return session
}

export function clearDevSession(): void {
  if (!isBrowser()) return
  window.localStorage.removeItem(STORAGE_KEY)
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

/**
 * Subscribe to dev-session changes. Returns an unsubscribe function.
 * Also reacts to localStorage changes from other tabs.
 */
export function onDevSessionChange(callback: () => void): () => void {
  if (!isBrowser()) return () => undefined
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) callback()
  }
  const onCustom = () => callback()
  window.addEventListener('storage', onStorage)
  window.addEventListener(CHANGE_EVENT, onCustom as EventListener)
  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(CHANGE_EVENT, onCustom as EventListener)
  }
}
